import React from 'react';
import { useNavigate } from 'react-router-dom';
import './AboutModerators.css';

const AboutModerators = () => {
  const navigate = useNavigate();

  return (
    <div className="moderators-page">
      <div className="moderators-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>SafeHer Moderators</h1>
        <p>Community Guardians Making SafeHer Safer</p>
      </div>

      <div className="moderators-content">
        {/* What is a Moderator */}
        <section className="mod-section">
          <h2>What is a Moderator?</h2>
          <p className="lead-text">
            Moderators are trusted community members who volunteer their time to keep information
            on SafeHer accurate and trustworthy. They review reports, remove spam or false posts,
            and communicate with reporters anonymously to gather more context.
            Moderators manage information on the platform only — they do not respond to physical
            situations or contact authorities on anyone's behalf.
          </p>
        </section>

        {/* What Moderators Do */}
        <section className="mod-section responsibilities">
          <h2>What Do Moderators Do?</h2>
          
          <div className="responsibilities-grid">
            <div className="responsibility-card">
              <div className="resp-icon">+</div>
              <h3>Review Reports</h3>
              <p>Verify that safety reports are accurate and follow community guidelines. Remove spam or false posts.</p>
            </div>

            <div className="responsibility-card">
              <div className="resp-icon">+</div>
              <h3>Support Users</h3>
              <p>Answer questions, provide guidance, and help users navigate the platform</p>
            </div>

            <div className="responsibility-card">
              <div className="resp-icon">+</div>
              <h3>Enforce Guidelines</h3>
              <p>Address inappropriate content and ensure community guidelines are followed</p>
            </div>

            <div className="responsibility-card">
              <div className="resp-icon">+</div>
              <h3>Build Community</h3>
              <p>Foster a positive, supportive environment where everyone feels safe to share</p>
            </div>
          </div>
        </section>

        {/* Why We Need Moderators */}
        <section className="mod-section why-section">
          <h2>Why Moderators Matter</h2>
          
          <div className="why-grid">
            <div className="why-item">
              <h3>Ensure Quality</h3>
              <p>Moderators help maintain accurate, trustworthy information that you can rely on</p>
            </div>

            <div className="why-item">
              <h3>Prevent Misuse</h3>
              <p>They catch false reports and inappropriate content before it affects the community</p>
            </div>

            <div className="why-item">
              <h3>Build Trust</h3>
              <p>Active moderation creates a safer, more reliable platform for everyone</p>
            </div>

            <div className="why-item">
              <h3>Quick Response</h3>
              <p>Moderators provide fast responses to issues and user concerns</p>
            </div>
          </div>
        </section>

        {/* Benefits of Being a Moderator */}
        <section className="mod-section benefits">
          <h2>Moderator Benefits</h2>
          
          <ul className="benefits-list">
            <li>
              <span className="benefit-icon">+</span>
              <div>
                <strong>Exclusive Moderator Badge</strong>
                <p>Stand out as a trusted community member with a special badge</p>
              </div>
            </li>
            <li>
              <span className="benefit-icon">+</span>
              <div>
                <strong>Direct Communication</strong>
                <p>Connect directly with users through the moderator messaging system</p>
              </div>
            </li>
            <li>
              <span className="benefit-icon">+</span>
              <div>
                <strong>Make a Real Impact</strong>
                <p>Help keep your community informed and the map accurate — meaningful work that matters</p>
              </div>
            </li>
            <li>
              <span className="benefit-icon">+</span>
              <div>
                <strong>Moderator Dashboard</strong>
                <p>Access special tools and insights to manage community safety</p>
              </div>
            </li>
          </ul>
        </section>

        {/* How to Become a Moderator */}
        <section className="mod-section apply-section">
          <h2>How to Become a Moderator</h2>
          
          <div className="apply-steps">
            <div className="apply-step">
              <div className="step-num">1</div>
              <div className="step-content">
                <h3>Be an Active User</h3>
                <p>Use SafeHer regularly for at least 30 days to understand the platform</p>
              </div>
            </div>

            <div className="apply-step">
              <div className="step-num">2</div>
              <div className="step-content">
                <h3>Submit Application</h3>
                <p>Click "Request Moderator Access" in your profile menu and tell us about yourself</p>
              </div>
            </div>

            <div className="apply-step">
              <div className="step-num">3</div>
              <div className="step-content">
                <h3>Get Approved</h3>
                <p>Our team reviews applications and notifies you of the decision</p>
              </div>
            </div>

            <div className="apply-step">
              <div className="step-num">4</div>
              <div className="step-content">
                <h3>Start Helping!</h3>
                <p>Access your moderator dashboard and begin making a difference</p>
              </div>
            </div>
          </div>

          <div className="apply-requirements">
            <h3>What We Look For</h3>
            <ul>
              <li>Active participation in the SafeHer community</li>
              <li>Understanding of community guidelines</li>
              <li>Commitment to fairness and impartiality</li>
              <li>Good communication skills</li>
              <li>Passion for community safety</li>
            </ul>
          </div>
        </section>

        {/* Code of Conduct */}
        <section className="mod-section conduct">
          <h2>Moderator Code of Conduct</h2>
          
          <div className="conduct-list">
            <div className="conduct-item">
              <h4>Be Fair and Impartial</h4>
              <p>Make decisions based on guidelines, not personal opinions</p>
            </div>
            
            <div className="conduct-item">
              <h4>Respect Privacy</h4>
              <p>Keep user information confidential and never share personal details</p>
            </div>
            
            <div className="conduct-item">
              <h4>Respond Promptly</h4>
              <p>Address reports and user questions in a timely manner</p>
            </div>
            
            <div className="conduct-item">
              <h4>Follow Guidelines</h4>
              <p>Always adhere to community guidelines and moderator policies</p>
            </div>
            
            <div className="conduct-item">
              <h4>Be Supportive</h4>
              <p>Treat all users with respect and empathy</p>
            </div>
            
            <div className="conduct-item">
              <h4>Stay Informed</h4>
              <p>Keep up with platform updates and best practices</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mod-section cta-section">
          <h2>Ready to Make a Difference?</h2>
          <p>Join our team of dedicated moderators and help keep SafeHer safe for everyone.</p>
          <button 
            className="apply-cta"
            onClick={() => navigate('/login')}
          >
            Login to Apply
          </button>
          <p className="cta-note">Don't have an account? <a href="/register" onClick={(e) => { e.preventDefault(); navigate('/register'); }}>Sign up here</a></p>
        </section>
      </div>
    </div>
  );
};

export default AboutModerators;
